import {
  AddressChangeChallengeMethod,
  AddressInput,
  AddressTypeInput,
  AddressValidationResultTypenames,
  ChannelType,
  Mutation,
  Query,
  SuggestedAddress,
  UpdateCustomerAddressChallenge,
  ValidAddress,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { MyVaroContext } from 'features/myVaro/provider/MyVaroContextProvider';
import areAddressesIdentical from 'features/personalDetails/utils/areAddressesIdentical';
import { getMutationErrorMessage } from 'features/personalDetails/utils/ChangeAddressScreen.util';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logEvent } from 'services/analytics/segment/segment';
import { UpdateCustomerAddress } from 'services/graphql/mutations/UpdateCustomerAddress';
import { UpdateCustomerAddresses } from 'services/graphql/mutations/UpdateCustomerAddresses';
import ValidateAddressV2 from 'services/graphql/queries/ValidateAddressV2';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import useCancellableMutation from 'shared/hooks/useCancellableMutation';
import useGetProfileInfo from 'shared/hooks/useGetProfileInfo/useGetProfileInfo';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import { useQuery } from 'urql';

interface AddressPayload {
  address: AddressInput;
  addressType: AddressTypeInput;
}

interface AddressSuccessDecisionInput {
  isMailingScreenEnabled: boolean;
  isMailingAddressDifferentThanResidential: boolean;
  isCheckedHomeAndMailingAddress?: boolean;
  isIdenticalHomeAndMailingAddress: boolean;
  isHomeAddressUpdated: boolean;
  isMailingAddressUpdated: boolean;
}

interface UseChangeAddressParams {
  channelType: ChannelType;
  isVloc: boolean;
  isLimitedAccess: boolean;
  fromConfirmAddress?: boolean;
  screenType?: string;
  setShowBottomSheet?: React.Dispatch<React.SetStateAction<boolean>>;
}

const useChangeAddress = ({
  channelType,
  isVloc,
  isLimitedAccess,
  fromConfirmAddress,
  screenType,
  setShowBottomSheet,
}: UseChangeAddressParams) => {
  const router = useRouter();
  const [updatedAddress, setUpdatedAddress] = useState<AddressInput | object>(
    {},
  );
  const {
    isCheckedHomeAndMailingAddress,
    setIsCheckedHomeAndMailingAddress,
    updatedAddressList,
    setUpdatedAddressList,
    setAddressVerification,
    isIdenticalHomeAndMailingAddress,
    setIsIdenticalHomeAndMailingAddress,
    recentlyBuiltAddressList,
    setRecentlyBuiltAddressList,
  } = useContext(MyVaroContext);

  const isHomeAddressScreenType = screenType === 'homeAddressScreen';

  const resolvedAddressType = isHomeAddressScreenType
    ? AddressTypeInput.HomeAddress
    : AddressTypeInput.MailingAddress;

  const hasSavedValidAddressRef = useRef(false);

  const appendUpdatedAddress = useCallback((address: AddressInput) => {
    setUpdatedAddress(address);
  }, []);

  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'personalDetails',
  });
  const { result } = useGetProfileInfo();
  const customerInfo = result?.customerInfo ?? null;
  const addressOnFile = customerInfo?.address;
  const mailingAddressOnFile = customerInfo?.mailingAddress;
  const isMailingAddressDifferentThanResidential =
    customerInfo?.isMailingAddressDifferentThanResidential ?? false;

  const { isFeatureEnabled } = useStatsig();
  const isMailingScreenEnabled = isFeatureEnabled(
    FeatureFlagKey.ENABLE_CUSTOMER_SERVICE_HOME_MAILING_ADDRESS,
  );

  const [
    { data: validateData, fetching: validateDataLoading },
    refetchValidateAddress,
  ] = useQuery<Query>({
    query: ValidateAddressV2,
    requestPolicy: 'network-only',
    variables: {
      input: {
        address: updatedAddress,
        channelType: channelType,
        type:
          screenType === 'mailingAddressScreen'
            ? 'CUSTOMER_MAILING'
            : 'CUSTOMER_HOME',
      },
    },
    pause: !updatedAddress || !Object.entries(updatedAddress).length,
  });

  const [mutationResult, executeUpdateAddress] =
    useCancellableMutation<Mutation>(
      isMailingScreenEnabled ? UpdateCustomerAddresses : UpdateCustomerAddress,
    );

  const showAddressVerification = useCallback(
    (
      addressType: AddressValidationResultTypenames,
      isLimitedAccess: boolean,
      isIdenticalAddressData: boolean = false,
    ) => {
      if (channelType === ChannelType.Customer) {
        const formAddress = updatedAddress as AddressInput;

        const updatedHomeAddress =
          updatedAddressList?.find(
            (item) => item.addressType === AddressTypeInput.HomeAddress,
          )?.address ?? customerInfo?.address;

        const comparisonAddress = isHomeAddressScreenType
          ? (customerInfo?.mailingAddress ?? customerInfo?.address)
          : updatedHomeAddress;

        setAddressVerification?.({
          fullAddress: isIdenticalAddressData
            ? {
                originalAddress: formAddress,
                suggestedAddress: comparisonAddress as AddressInput,
              }
            : (validateData?.validateAddressV2 as SuggestedAddress),
          addressType: addressType,
          isVloc,
          isLimitedAccess,
          fromConfirmAddress,
          screenType,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      validateData?.validateAddressV2,
      setAddressVerification,
      channelType,
      isVloc,
      fromConfirmAddress,
      updatedAddress,
      customerInfo?.mailingAddress,
      customerInfo?.address,
      isHomeAddressScreenType,
      translatedText,
      screenType,
      isCheckedHomeAndMailingAddress,
      updatedAddressList,
    ],
  );

  const buildUpdatedAddressList = useCallback(
    (externalId?: string): AddressPayload[] => {
      const newEntry: AddressPayload = {
        address: {
          ...(updatedAddress as AddressInput),
          externalId,
        },
        addressType: resolvedAddressType,
      };

      const newAddressList: AddressPayload[] = [
        ...(updatedAddressList ?? []).filter(
          (item) => item.addressType !== newEntry.addressType,
        ),
        newEntry,
      ];

      if (!hasSavedValidAddressRef.current) {
        hasSavedValidAddressRef.current = true;
        setUpdatedAddressList?.(newAddressList);
      }

      return newAddressList;
    },
    [
      updatedAddress,
      updatedAddressList,
      resolvedAddressType,
      setUpdatedAddressList,
    ],
  );

  const executeUpdateAddressList = useCallback(
    async (externalId?: string, addressList?: AddressPayload[]) => {
      return executeUpdateAddress({
        channelType: 'IN_APP',
        addressInputs: addressList,
        externalId,
        isMailingAddressDifferentThanResidential:
          isIdenticalHomeAndMailingAddress
            ? false
            : (isCheckedHomeAndMailingAddress ?? false),
      });
    },
    [
      executeUpdateAddress,
      isCheckedHomeAndMailingAddress,
      isIdenticalHomeAndMailingAddress,
    ],
  );

  const executeSingleAddressUpdate = useCallback(
    async (externalId?: string) => {
      return executeUpdateAddress({
        channelType: '',
        address: updatedAddress,
        externalId,
        addressType: resolvedAddressType,
        isMailingAddressDifferentThanResidential:
          isIdenticalHomeAndMailingAddress
            ? false
            : (isCheckedHomeAndMailingAddress ?? false),
      });
    },
    [
      executeUpdateAddress,
      updatedAddress,
      resolvedAddressType,
      isCheckedHomeAndMailingAddress,
      isIdenticalHomeAndMailingAddress,
    ],
  );

  const findAddressByType = (
    list?: AddressPayload[],
    type?: AddressTypeInput,
  ) => list?.find((item) => item.addressType === type)?.address;

  const getAddressSuccessDecision = useCallback(
    ({
      isMailingScreenEnabled,
      isMailingAddressDifferentThanResidential,
      isCheckedHomeAndMailingAddress,
      isIdenticalHomeAndMailingAddress,
      isHomeAddressUpdated,
      isMailingAddressUpdated,
    }: AddressSuccessDecisionInput): {
      msgKey: string;
      subcategory: string;
    } => {
      let msgKey = 'homeAddressSuccess';
      let subcategory = 'home_address_changed';

      if (!isMailingScreenEnabled) {
        return { msgKey, subcategory };
      }

      const bothUpdated = isHomeAddressUpdated && isMailingAddressUpdated;
      const noneUpdated = !isHomeAddressUpdated && !isMailingAddressUpdated;
      const onlyHomeUpdated = isHomeAddressUpdated && !isMailingAddressUpdated;
      const onlyMailingUpdated =
        !isHomeAddressUpdated && isMailingAddressUpdated;

      if (!isMailingAddressDifferentThanResidential) {
        const shouldShowHomeAndMailing =
          (onlyHomeUpdated && !isCheckedHomeAndMailingAddress) ||
          (isMailingAddressUpdated && isIdenticalHomeAndMailingAddress) ||
          (bothUpdated && isCheckedHomeAndMailingAddress);

        const shouldShowMailing =
          (noneUpdated && !isCheckedHomeAndMailingAddress) ||
          onlyMailingUpdated;

        if (shouldShowHomeAndMailing || noneUpdated) {
          return {
            msgKey: 'homeAndMailingAddressSuccess',
            subcategory: 'home_and_mailing_address_changed',
          };
        }

        if (shouldShowMailing) {
          return {
            msgKey: 'mailingAddressSuccess',
            subcategory: 'mailing_address_changed',
          };
        }
      } else {
        if (
          (isHomeAddressUpdated && isIdenticalHomeAndMailingAddress) ||
          (!isHomeAddressUpdated && isHomeAddressScreenType)
        ) {
          return {
            msgKey: 'homeAddressSuccess',
            subcategory: 'home_address_changed',
          };
        }

        if (isHomeAddressUpdated && !isCheckedHomeAndMailingAddress) {
          return {
            msgKey: 'homeAndMailingAddressSuccess',
            subcategory: 'home_and_mailing_address_changed',
          };
        }

        if (!isHomeAddressUpdated && !isHomeAddressScreenType) {
          return {
            msgKey: 'mailingAddressSuccess',
            subcategory: 'mailing_address_changed',
          };
        }
      }

      return { msgKey, subcategory };
    },
    [isHomeAddressScreenType],
  );

  const handleSuccessNavigation = useCallback(
    (newAddressList) => {
      if (!fromConfirmAddress) {
        const newHomeAddress = findAddressByType(
          newAddressList,
          AddressTypeInput.HomeAddress,
        );

        const newMailingAddress = findAddressByType(
          newAddressList,
          AddressTypeInput.MailingAddress,
        );

        const isHomeAddressUpdated = Boolean(
          newHomeAddress &&
          !areAddressesIdentical(addressOnFile, newHomeAddress),
        );
        const isMailingAddressUpdated = Boolean(
          newMailingAddress &&
          !areAddressesIdentical(mailingAddressOnFile, newMailingAddress),
        );

        const { msgKey, subcategory } = getAddressSuccessDecision({
          isMailingScreenEnabled,
          isMailingAddressDifferentThanResidential,
          isCheckedHomeAndMailingAddress,
          isIdenticalHomeAndMailingAddress: Boolean(
            isIdenticalHomeAndMailingAddress,
          ),
          isHomeAddressUpdated,
          isMailingAddressUpdated,
        });

        void logEvent('Address Successfully Changed', {
          category: 'profile_settings',
          subcategory,
        });

        if (isLimitedAccess) {
          return router.dismissTo({
            pathname: '/reactivation',
          });
        } else if (isVloc) {
          return router.dismissTo({
            pathname: '/varo-line-of-credit/user-info',
          });
        }
        const routerParams: { refresh: string; msgKey?: string } = {
          refresh: 'true',
        };
        if (msgKey) {
          routerParams.msgKey = msgKey;
        }
        return router.dismissTo({
          pathname: '/myvaro/personal-details',
          params: routerParams,
        });
      } else if (fromConfirmAddress) {
        return router.push({
          pathname: '/confirm-your-address/success',
          params: { fromConfirmAddress: 'true' },
        });
      }
    },
    [
      fromConfirmAddress,
      isLimitedAccess,
      isVloc,
      isCheckedHomeAndMailingAddress,
      isMailingAddressDifferentThanResidential,
      addressOnFile,
      mailingAddressOnFile,
      isIdenticalHomeAndMailingAddress,
      isMailingScreenEnabled,
      getAddressSuccessDecision,
      ,
      router,
    ],
  );

  const handleRiskChallengeNavigation = useCallback(
    (challengeParams?: UpdateCustomerAddressChallenge[]) => {
      const emailChallengeId = challengeParams?.find(
        (challenge) =>
          challenge?.challengeType === AddressChangeChallengeMethod.Email,
      )?.challengeId;

      const smsChallengeId = challengeParams?.find(
        (challenge) =>
          challenge?.challengeType === AddressChangeChallengeMethod.Sms,
      )?.challengeId;

      if (emailChallengeId && smsChallengeId) {
        router.navigate({
          pathname: '/verify-address-change/confirm-challenge-method',
          params: {
            emailChallengeId,
            smsChallengeId,
            screenType,
          },
        });
        return;
      }

      if (emailChallengeId && !smsChallengeId) {
        router.navigate({
          pathname: '/verify-address-change/enter-email-otp',
          params: { challengeId: emailChallengeId, screenType },
        });
        return;
      }

      if (smsChallengeId) {
        router.navigate({
          pathname: '/verify-address-change/enter-sms-otp',
          params: { challengeId: smsChallengeId, screenType },
        });
      }
    },
    [screenType, router],
  );

  const executeRiskUpdateAddress = useCallback(
    async (challengeId: string) => {
      return executeUpdateAddress({
        channelType: 'IN_APP',
        challengeId,
      });
    },
    [executeUpdateAddress],
  );

  const handleRiskUpdateAddress = useCallback(
    async (challengeId: string) => {
      try {
        const addressResult = await executeRiskUpdateAddress(challengeId);

        const { isError, isSuccessful, errorMessage } = getMutationErrorMessage(
          addressResult?.data?.updateCustomerAddress,
          addressResult?.data?.updateCustomerAddresses,
        );

        if (isError) {
          router.dismissTo({
            pathname: '/myvaro/personal-details',
            params: { snackbarMsg: errorMessage, refresh: 'true' },
          });
          return;
        }

        if (isSuccessful) {
          const addressListToUse = recentlyBuiltAddressList;
          handleSuccessNavigation(addressListToUse);
          setRecentlyBuiltAddressList?.(undefined);
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }

        const errorMsg = error instanceof Error ? error.message : String(error);

        router.dismissTo({
          pathname: '/myvaro/personal-details',
          params: { snackbarMsg: errorMsg, refresh: 'true' },
        });
      }
    },
    [
      executeRiskUpdateAddress,
      handleSuccessNavigation,
      recentlyBuiltAddressList,
      setRecentlyBuiltAddressList,
      ,
      router,
    ],
  );

  const handleValidAddressUpdate = useCallback(
    async (externalId?: string, newAddressList?: AddressPayload[]) => {
      try {
        const addressResult = isMailingScreenEnabled
          ? await executeUpdateAddressList(externalId, newAddressList)
          : await executeSingleAddressUpdate(externalId);

        if (typeof setUpdatedAddressList === 'function') {
          setUpdatedAddressList([]);
        }

        const {
          isError,
          isSuccessful,
          isDeclined,
          errorMessage,
          isChallenged,
          challengeParams,
        } = getMutationErrorMessage(
          addressResult?.data?.updateCustomerAddress,
          addressResult?.data?.updateCustomerAddresses,
        );

        if (isChallenged) {
          handleRiskChallengeNavigation(challengeParams);
        }

        if (isDeclined) {
          router.navigate({
            pathname:
              '/myvaro/personal-details/change-address/declined/address-decline',
          });
          return;
        }

        if (isError) {
          router.dismissTo({
            pathname: '/myvaro/personal-details',
            params: { snackbarMsg: errorMessage },
          });
          return;
        }

        if (isSuccessful) {
          handleSuccessNavigation(newAddressList);
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }

        const errorMsg = error instanceof Error ? error.message : String(error);

        router.dismissTo({
          pathname: '/myvaro/personal-details',
          params: { snackbarMsg: errorMsg },
        });
      }
    },
    [
      executeSingleAddressUpdate,
      executeUpdateAddressList,
      handleSuccessNavigation,
      isMailingScreenEnabled,
      setUpdatedAddressList,
      handleRiskChallengeNavigation,
      ,
      router,
    ],
  );

  const previousValidateDataRef = useRef<Query | undefined>(undefined);

  useEffect(() => {
    hasSavedValidAddressRef.current = false;
  }, [updatedAddress]);

  useEffect(() => {
    if (validateData !== previousValidateDataRef.current) {
      previousValidateDataRef.current = validateData;
      switch (validateData?.validateAddressV2.__typename) {
        case AddressValidationResultTypenames.AddressValidationError:
          setIsIdenticalHomeAndMailingAddress?.(false);
          router.dismissTo({
            pathname: '/myvaro/personal-details',
            params: {
              snackbarMsg: validateData.validateAddressV2.message,
            },
          });
          setShowBottomSheet?.(false);
          break;
        case AddressValidationResultTypenames.SuggestedAddress:
          setIsIdenticalHomeAndMailingAddress?.(false);
          showAddressVerification(
            AddressValidationResultTypenames.SuggestedAddress,
            isLimitedAccess,
          );
          setShowBottomSheet?.(true);
          break;
        case AddressValidationResultTypenames.InvalidAddress:
          setIsIdenticalHomeAndMailingAddress?.(false);
          showAddressVerification(
            AddressValidationResultTypenames.InvalidAddress,
            isLimitedAccess,
          );
          setShowBottomSheet?.(true);
          break;
        case AddressValidationResultTypenames.AllowOverrideAddress:
          setIsIdenticalHomeAndMailingAddress?.(false);
          showAddressVerification(
            AddressValidationResultTypenames.AllowOverrideAddress,
            isLimitedAccess,
          );
          setShowBottomSheet?.(true);
          break;
        case AddressValidationResultTypenames.ValidAddress: {
          const validateResponse =
            validateData.validateAddressV2 as ValidAddress;
          const externalId =
            validateResponse?.originalAddress?.externalId ?? undefined;
          const newAddressList = buildUpdatedAddressList(externalId);
          setRecentlyBuiltAddressList?.(newAddressList);

          if (
            isHomeAddressScreenType &&
            isCheckedHomeAndMailingAddress &&
            !isMailingAddressDifferentThanResidential
          ) {
            setUpdatedAddress({});
            router.push(
              '/myvaro/personal-details/change-mailing-address?analyticsKey=changeMailingAddress',
            );
            return;
          }

          // Address passed validation — now check if identical to mailing
          if (isMailingScreenEnabled && isIdenticalHomeAndMailingAddress) {
            showAddressVerification(
              AddressValidationResultTypenames.ValidAddress,
              isLimitedAccess,
              true,
            );
            setShowBottomSheet?.(true);
            break;
          }

          handleValidAddressUpdate(externalId, newAddressList);
          break;
        }
        default:
          break;
      }
    }
  }, [
    validateData,
    executeUpdateAddress,
    showAddressVerification,
    translatedText,
    updatedAddress,
    isLimitedAccess,
    isVloc,
    fromConfirmAddress,
    screenType,
    isCheckedHomeAndMailingAddress,
    isHomeAddressScreenType,
    isMailingAddressDifferentThanResidential,
    isIdenticalHomeAndMailingAddress,
    setShowBottomSheet,
    setUpdatedAddressList,
    updatedAddressList,
    isMailingScreenEnabled,
    resolvedAddressType,
    setIsCheckedHomeAndMailingAddress,
    setIsIdenticalHomeAndMailingAddress,
    executeSingleAddressUpdate,
    executeUpdateAddressList,
    handleSuccessNavigation,
    buildUpdatedAddressList,
    handleValidAddressUpdate,
    setRecentlyBuiltAddressList,
    router,
  ]);

  const triggerUpdate = useCallback(
    (address?: AddressInput) => {
      const validateResponse = validateData?.validateAddressV2;
      const externalId =
        validateResponse?.__typename ===
        AddressValidationResultTypenames.ValidAddress
          ? (validateResponse.originalAddress?.externalId ?? undefined)
          : undefined;

      if (address) {
        const newAddressList: AddressPayload[] = [
          {
            address,
            addressType: AddressTypeInput.HomeAddress,
          },
          {
            address,
            addressType: AddressTypeInput.MailingAddress,
          },
        ];
        setUpdatedAddressList?.(newAddressList);
        handleValidAddressUpdate(externalId, newAddressList);
      } else {
        const newAddressList = buildUpdatedAddressList();
        handleValidAddressUpdate(externalId, newAddressList);
      }
    },
    [
      validateData,
      buildUpdatedAddressList,
      handleValidAddressUpdate,
      setUpdatedAddressList,
    ],
  );

  return {
    loading: mutationResult.fetching || validateDataLoading,
    validateResponse: validateData,
    updateResponse: mutationResult,
    setUpdatedAddress: appendUpdatedAddress,
    refetchValidateAddress,
    triggerUpdate,
    handleRiskUpdateAddress,
    handleValidAddressUpdate,
  };
};

export default useChangeAddress;
